/**
 * Landing page for OIDC redirect callbacks.
 * URL: /auth/callback#token=<access_token>
 *
 * The token rides in the fragment, which no server, proxy log or Referer ever
 * sees (Sep 28 2026 pentest AUTH-VULN-01). It is scrubbed from the address bar
 * and history before anything else runs, then stored in memory.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { takeAuthorizeReturn } from '../modules/task_mining/connect/authReturn';
import { takeReturnPath } from '../lib/authReturn';
import { useNavigationStore } from '../store/navigationStore';
import MaicBrand from '../components/MaicBrand';
import { usePublicInterface } from './publicUi';
import './public.css';

const SSOCallbackPage: React.FC = () => {
  const { t } = useTranslation();
  const { handleOIDCCallback } = useAuthStore();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  // No session yet: v2 only when this browser forces it (pages/publicUi.ts).
  const v2 = usePublicInterface();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    // The query-string fallback only covers a backend still on the old
    // redirect during a rolling deploy.
    const token = fragment.get('token') || params.get('token');
    const error = params.get('error');
    // Scrub before anything else: the token must not stay in the address bar
    // or in session history.
    if (token) window.history.replaceState({}, '', window.location.pathname);

    if (error) {
      setErrorMessage(`SSO error: ${error}`);
      setStatus('error');
      return;
    }

    if (!token) {
      setErrorMessage(t('auth.noTokenReceived', 'No token received from SSO provider.'));
      setStatus('error');
      return;
    }

    handleOIDCCallback(token)
      .then(() => {
        setStatus('success');
        // Clean URL and go back to where the person asked to go: a
        // task-mining device authorization, or the page parked by the login
        // page before leaving for the provider (GSL-4, lib/authReturn).
        // Home when there is none.
        setTimeout(() => {
          const back = takeAuthorizeReturn();
          const parked = takeReturnPath();
          window.history.replaceState({}, '', back ?? parked ?? '/');
          // The shell is not mounted yet, so it would miss this popstate:
          // point the navigation store at the restored page directly.
          useNavigationStore.getState().syncFromLocation();
          window.dispatchEvent(new Event('popstate'));
        }, 800);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : t('auth.failedProcessSso', 'Failed to process SSO login');
        setErrorMessage(msg);
        setStatus('error');
      });
  }, [handleOIDCCallback, t]);

  if (v2) {
    return (
      <div className="pub-page">
        <div className="pub-card pub-status" data-testid="sso-callback">
          <MaicBrand className="pub-brand" />
          {status === 'loading' && (
            <>
              <div className="pub-status__title">{t('auth.completingSignIn', 'Completing sign-in…')}</div>
              <div className="pub-status__text">{t('auth.pleaseWait', 'Please wait')}</div>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="pub-status__title" data-state="success">{t('auth.signedInSuccessfully', 'Signed in successfully')}</div>
              <div className="pub-status__text">{t('auth.redirecting', 'Redirecting…')}</div>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="pub-status__title" data-state="error" role="alert">{t('auth.signInFailed', 'Sign-in failed')}</div>
              <div className="pub-status__text">{errorMessage}</div>
              <button type="button" className="ds-btn ds-btn--primary" onClick={() => { window.location.href = '/'; }}>
                {t('auth.backToLogin', 'Back to login')}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#1B2333',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif',
    }}>
      <div style={{ textAlign: 'center', color: '#8A9BAE' }}>
        {status === 'loading' && (
          <>
            <div style={{ fontSize: 14, marginBottom: 8 }}>{t('auth.completingSignIn', 'Completing sign-in…')}</div>
            <div style={{ fontSize: 11, color: '#475569' }}>{t('auth.pleaseWait', 'Please wait')}</div>
          </>
        )}
        {status === 'success' && (
          <>
            <div style={{ fontSize: 14, color: '#10B981', marginBottom: 8 }}>{t('auth.signedInSuccessfully', 'Signed in successfully')}</div>
            <div style={{ fontSize: 11, color: '#475569' }}>{t('auth.redirecting', 'Redirecting…')}</div>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ fontSize: 14, color: '#F87171', marginBottom: 8 }}>{t('auth.signInFailed', 'Sign-in failed')}</div>
            <div style={{ fontSize: 11, color: '#475569', maxWidth: 300 }}>{errorMessage}</div>
            <button
              onClick={() => { window.location.href = '/'; }}
              style={{
                marginTop: 16, height: 30, padding: '0 14px',
                backgroundColor: '#4054A8', color: '#BCC8E0',
                border: 'none', borderRadius: 3, fontSize: 12, cursor: 'pointer',
              }}
            >
              {t('auth.backToLogin', 'Back to login')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SSOCallbackPage;
